import { saveInventoryDevice } from 'modules/inventory/sensorInventory';
import { Button as AddButton } from 'components/ui/button';
import { Plus } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Modal, Tag } from 'antd';
import { useSelector } from 'react-redux';
import { AppState } from '../../../store';
import { useTranslation } from 'react-i18next';
import firebase from 'firebase/compat/app';
import {
  InventoryStatus,
  InventoryStatusMap,
  InventoryContextForType,
  InventoryType,
} from '../../../modules/inventory/types';
import Message from '../../../components/Notification/Message';
import TextArea from 'antd/lib/input/TextArea';

const FormItem = Form.Item;
const Option = Select.Option;
const formItemLayout = {
  wrapperCol: { span: 24 },
};

export default function AddForm({
  type = InventoryType.ESP_AQ,
  simcards,
  inventoryTagsMap,
}: {
  type?: InventoryType.ESP_AQ | InventoryType.MACHINE_ACTIVITY;
  simcards: InventoryContextForType<InventoryType.SIM_CARDS>[];
  inventoryTagsMap: { [id: string]: { id: string; title: string } };
}) {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [inventoryId, setInventoryId] = useState(
    () => firebase.firestore().collection('inventory').doc().id
  );
  const orgs = useSelector((state: AppState) =>
    state.orgs.orgs
      .map(({ id, title, groupId }: { id: string; title: string; groupId?: string | null }) => ({
        id,
        title,
        groupId,
      }))
      .sort((a: any, b: any) => (a?.title?.toLowerCase() > b?.title?.toLowerCase() ? 1 : -1))
  );
  const groupMap = useSelector((state: AppState) => state.groups.groupsMap);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    form.validateFields();
    return () => {};
  }, []);

  const addInventoryItem = async (
    values: Partial<InventoryContextForType<InventoryType.ESP_AQ>>
  ) => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await saveInventoryDevice({
        ...values,
        id: inventoryId,
        type,
        sim: values.sim || null,
      });
      onClose();
      form.resetFields();
      setInventoryId(firebase.firestore().collection('inventory').doc().id);
      Message({ key: 'addInventory', message: 'Lager og SIM er gemt' });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Kunne ikke gemme. Prøv igen.');
    } finally {
      setSaving(false);
    }
  };

  const onClose = () => setVisible(false);

  const options = simcards.map(({ id: simId, iccid, internalId, phoneNumber, connection }) => ({
    label: [iccid, internalId, phoneNumber, connection?.title].filter(Boolean).join(' · '),
    value: simId,
    disabled: Boolean(connection?.deviceId),
  }));

  return (
    <>
      <AddButton onClick={() => setVisible(true)}>
        <Plus aria-hidden="true" />
        {
          // TODO: maybe translate?
          'Tilføj Sensor'
        }
      </AddButton>
      <Modal
        title="Tilføj Sensor"
        open={!!visible}
        confirmLoading={saving}
        onCancel={onClose}
        footer={[]}
      >
        <Form form={form} onFinish={addInventoryItem}>
          <FormItem
            {...formItemLayout}
            name="deviceId"
            rules={[{ required: true, message: 'Please input device id!' }]}
          >
            <Input type="text" placeholder={t('inventory.ipad.serial')} />
          </FormItem>

          <FormItem
            {...formItemLayout}
            name="name"
            rules={[{ required: true, message: 'Please input name!' }]}
          >
            <Input type="text" placeholder={t('inventory.ipad.name')} />
          </FormItem>
          <FormItem name="internalId" label="Internt ID">
            <Input placeholder="Fx 87003" maxLength={100} />
          </FormItem>
          <FormItem
            {...formItemLayout}
            name="sim"
            rules={[{ required: false, message: 'Please input name!' }]}
          >
            <Select
              showSearch
              allowClear
              placeholder={t('inventory.ipad.sim')}
              optionFilterProp="children"
              filterOption={(input, option) =>
                // @ts-ignore
                (option?.label ?? '').includes(input)
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? '')
                  // @ts-ignore
                  .toLowerCase()
                  // @ts-ignore
                  .localeCompare((optionB?.label ?? '').toLowerCase())
              }
              options={options}
            />
          </FormItem>

          <FormItem
            {...formItemLayout}
            style={{ minWidth: '150px' }}
            name="status"
            rules={[{ required: true, message: 'Please select status!' }]}
          >
            <Select placeholder="Select status">
              <Option value={InventoryStatus.NEEDS_ORDER}>
                <Tag color="red">{InventoryStatusMap[InventoryStatus.NEEDS_ORDER]}</Tag>
              </Option>
              <Option value={InventoryStatus.ORDERED}>
                <Tag color="gold">{InventoryStatusMap[InventoryStatus.ORDERED]}</Tag>
              </Option>
              <Option value={InventoryStatus.IN_STORAGE}>
                <Tag color="orange">{InventoryStatusMap[InventoryStatus.IN_STORAGE]}</Tag>
              </Option>
              <Option value={InventoryStatus.CONFIG}>
                <Tag color="blue-inverse">{InventoryStatusMap[InventoryStatus.CONFIG]}</Tag>
              </Option>
              <Option value={InventoryStatus.READY}>
                <Tag color="blue">{InventoryStatusMap[InventoryStatus.READY]}</Tag>
              </Option>
              <Option value={InventoryStatus.ASSIGNED}>
                <Tag color="green">{InventoryStatusMap[InventoryStatus.ASSIGNED]}</Tag>
              </Option>
            </Select>
          </FormItem>
          <FormItem
            {...formItemLayout}
            style={{ minWidth: '150px' }}
            name="tags"
            rules={[{ required: false, message: 'Please select status!' }]}
          >
            <Select placeholder={t('Tags')} mode="multiple">
              {Object.entries(inventoryTagsMap).map(([key, value]) => (
                <Option value={key}>{value.title}</Option>
              ))}
            </Select>
          </FormItem>
          <FormItem
            {...formItemLayout}
            style={{ minWidth: '150px' }}
            name="orgId"
            rules={[{ required: true, message: 'Please select company!' }]}
          >
            <Select
              showSearch
              allowClear
              style={{ width: '100%' }}
              placeholder="Select a company"
              optionFilterProp="children"
              filterOption={(input, option) =>
                // @ts-ignore
                option?.children
                  // @ts-ignore
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              {orgs.map((org: { id: string; title: string; groupId?: string | null }) => (
                <Option value={org.id}>
                  {`${org.title} ${org?.groupId ? `(${groupMap[org.groupId]?.title})` : ''}`}
                </Option>
              ))}
            </Select>
          </FormItem>
          <FormItem
            {...formItemLayout}
            name="note"
            rules={[{ required: false, message: 'Please input device id!' }]}
          >
            <TextArea placeholder={t('Note')} />
          </FormItem>
          <FormItem>
            <Button type="primary" block htmlType="submit" loading={saving}>
              {'Tilføj'}
            </Button>
          </FormItem>
        </Form>
        {error && (
          <div role="alert" style={{ color: 'red' }}>
            {error}
          </div>
        )}
      </Modal>
    </>
  );
}
